import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../../auth/JWTAuthContext';
import { useJWTSync } from '../../auth/useJWTSync';
import { useEntitlements } from '../../EntitlementsContext';
import { useToast } from '../../contexts/ToastContext';
import { socketService } from '../../services/SocketService';
import XPProgressBar from './XPProgressBar';
import AttributePanel from './AttributePanel';
import StrategySliders from './StrategySliders';
import BattleHistoryTable from './BattleHistoryTable';
import LootTab from './LootTab';

const HeroDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders, token } = useJWTAuth();
  const { jwtUser } = useJWTSync(); // Sync JWT with entitlements
  const { hasEntitlement } = useEntitlements();
  const { success, error, warning } = useToast();
  const [hero, setWarrior] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isOwner, setIsOwner] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const fileInputRef = useRef(null);
  
  const hasStatReset = hasEntitlement('hero-stats-reset');

  // Frequency helper functions
  const getFrequencyLabel = (frequency) => {
    switch (frequency) {
      case 0: return 'Never';
      case 3: return 'Seldom';
      case 6: return 'Often';
      case 11: return 'Frequent';
      default: return 'Often';
    }
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency) {
      case 0: return 'bg-red-500 hover:bg-red-600';
      case 3: return 'bg-orange-500 hover:bg-orange-600';
      case 6: return 'bg-yellow-500 hover:bg-yellow-600';
      case 11: return 'bg-green-500 hover:bg-green-600';
      default: return 'bg-yellow-500 hover:bg-yellow-600';
    }
  };

  const getFrequencyColorDimmed = (frequency) => {
    switch (frequency) {
      case 0: return 'bg-red-500/10 hover:bg-red-500/30 border border-red-500/30';
      case 3: return 'bg-orange-500/10 hover:bg-orange-500/30 border border-orange-500/30';
      case 6: return 'bg-yellow-500/10 hover:bg-yellow-500/30 border border-yellow-500/30';
      case 11: return 'bg-green-500/10 hover:bg-green-500/30 border border-green-500/30';
      default: return 'bg-yellow-500/10 hover:bg-yellow-500/30 border border-yellow-500/30';
    }
  };

  // Connect to socket when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated() && token && !socketService.isSocketConnected()) {
      console.log('🔌 [HeroDetail] Connecting to socket server...');
      socketService.connect(token);
    }
    
    return () => {
      // Don't disconnect socket here - let it stay connected for other hero pages
      // Socket will be disconnected when user leaves the Heroes section entirely
    };
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchWarrior();
  }, [id, user]);

  const fetchWarrior = async () => {
    try {
      const response = await fetch(`/api/heroes/${id}`);
      const data = await response.json();
      
      if (data.success) {
        setWarrior(data.data);
        // Store original image URL when hero is first loaded
        if (!originalImageUrl) {
          setOriginalImageUrl(data.data.image_url || 'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy1.png');
        }
        // Check if current user is the owner
        const ownerCheck = isAuthenticated() && user && data.data.owner_id === user.id;
        console.log('🔍 [HeroDetail] Owner check:', {
          isAuthenticated: isAuthenticated(),
          user: user,
          userId: user?.id,
          warriorOwnerId: data.data.owner_id,
          isOwner: ownerCheck
        });
        setIsOwner(ownerCheck);
      } else {
        console.error('Failed to fetch hero:', data.error);
        navigate('/heroes');
      }
    } catch (error) {
      console.error('Error fetching hero:', error);
      navigate('/heroes');
    } finally {
      setLoading(false);
    }
  };

  const handleAttributeUpdate = (updatedWarrior) => {
    setWarrior(updatedWarrior);
  };

  const handleStrategyUpdate = (updatedWarrior) => {
    setWarrior(updatedWarrior);
  };

  const handleFrequencyUpdate = async (frequency) => {
    try {
      if (!isAuthenticated()) {
        error('Please log in to update hero frequency');
        return;
      }

      const response = await fetch(`/api/heroes/${hero.id}/frequency`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ frequency })
      });

      const data = await response.json();
      
      if (data.success) {
        setWarrior(prev => ({ ...prev, frequency }));
        success('Hero frequency updated successfully');
      } else {
        error(`Failed to update frequency: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating frequency:', error);
      error('Error updating frequency. Please try again.');
    }
  };

  const handleDeleteWarrior = async () => {
    if (window.confirm(`Are you sure you want to delete "${hero.name}"? This action cannot be undone.`)) {
      try {
        const response = await fetch(`/api/heroes/${hero.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_API_KEY || 'soulwarz',
            ...getAuthHeaders()
          }
        });

        if (response.ok) {
          // Redirect back to heroes list
          navigate('/heroes');
        } else {
          const data = await response.json();
          error(`Failed to delete hero: ${data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting hero:', error);
        error('Failed to delete hero. Please try again.');
      }
    }
  };

  const handleResetStats = async () => {
    if (!hasStatReset) {
      warning('You do not have the hero-stats-reset entitlement.');
      return;
    }

    const confirmMessage = `Are you sure you want to reset all spent XP for "${hero.name}"?\n\n` +
      `This will:\n` +
      `• Refund all XP spent on attributes (${hero.total_xp - hero.unspent_xp} XP)\n` +
      `• Reset all primary and secondary attribute spent points to 0\n` +
      `• Recalculate all attributes to base values\n\n` +
      `This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      setResetting(true);
      try {
        const response = await fetch(`/api/heroes/${hero.id}/reset-stats`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.success) {
          success(`Stats reset successfully! ${data.refunded_xp} XP has been refunded.`);
          // Refresh hero data
          await fetchWarrior();
        } else {
          error(`Failed to reset stats: ${data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error resetting stats:', error);
        error('Failed to reset stats. Please try again.');
      } finally {
        setResetting(false);
      }
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', ownerOnly: false },
    { id: 'attributes', name: 'Attributes', ownerOnly: true },
    { id: 'strategies', name: 'Strategies', ownerOnly: true },
    { id: 'loot', name: 'Loot', ownerOnly: true },
    { id: 'history', name: 'History', ownerOnly: false },
    { id: 'stats', name: 'Settings', ownerOnly: false }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading Hero...</div>
      </div>
    );
  }

  if (!hero) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Hero not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-2">
          <button
            onClick={() => navigate('/heroes')}
            className="text-blue-400 hover:text-blue-300 mb-4 flex items-center"
          >
            ← Heroes
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{hero.name}</h1>
              <p className="text-gray-400">
                Owned by {hero.owner_username} • Level {hero.level}
                {hero.is_active && <span className="ml-2 text-blue-400 font-semibold">• Active</span>}
              </p>
            </div>
            
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 mb-6">
          <nav className="flex flex-wrap gap-x-4 gap-y-2">
            {tabs.map(tab => {
              if (tab.ownerOnly && !isOwner) return null;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex-1 min-w-[calc(30%-0.5rem)] md:flex-none md:min-w-0 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <XPProgressBar
                currentXP={hero.unspent_xp}
                totalXP={hero.total_xp}
                level={hero.level}
                showDetails={true}
                onSpendXP={isOwner ? () => setActiveTab('attributes') : null}
              />
              
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Battle Statistics</h3>
                {hero.battleStats ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-400">{hero.battleStats.total_battles}</div>
                      <div className="text-sm text-gray-400">Total Battles</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-400">{hero.battleStats.wins}</div>
                      <div className="text-sm text-gray-400">Wins</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-400">{hero.battleStats.total_eliminations}</div>
                      <div className="text-sm text-gray-400">Eliminations</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-cyan-400">
                        {hero.battleStats.avg_position ? hero.battleStats.avg_position.toFixed(1) : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-400">Avg Position</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-400 mb-6">No battle statistics available</div>
                )}
              </div>
              
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex flex-col items-center gap-4">
                  {/* Current PFP with Edit button overlay */}
                  <div className="relative">
                    <img
                      src={avatarPreview || originalImageUrl || hero.image_url || 'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy1.png'}
                      alt={hero.name}
                      className="w-64 h-64 rounded-full object-cover border-4 border-gray-600"
                      onError={(e) => {
                        e.currentTarget.src = 'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy1.png';
                      }}
                    />
                    {/* Hidden file input */}
                    {isOwner && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              // Store original image URL when first selecting a file (if not already stored)
                              if (!originalImageUrl) {
                                setOriginalImageUrl(hero.image_url || 'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy1.png');
                              }
                              
                              // Validate file size (5MB)
                              if (file.size > 5 * 1024 * 1024) {
                                warning('File size exceeds 5MB limit');
                                e.target.value = '';
                                setAvatarFile(null);
                                setAvatarPreview(null);
                                return;
                              }
                              // Validate file type
                              const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
                              if (!validTypes.includes(file.type)) {
                                warning('Invalid file type. Only JPEG and PNG images are allowed.');
                                e.target.value = '';
                                setAvatarFile(null);
                                setAvatarPreview(null);
                                return;
                              }
                              // Create preview
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setAvatarPreview(reader.result);
                              };
                              reader.readAsDataURL(file);
                              setAvatarFile(file);
                              setShowHelpText(false); // Hide help text when file is selected
                            }
                          }}
                          className="hidden"
                        />
                        {/* Edit button overlay */}
                        {!avatarFile && (
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            onMouseEnter={() => setShowHelpText(true)}
                            onMouseLeave={() => setShowHelpText(false)}
                            onTouchStart={() => setShowHelpText(true)}
                            className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-colors text-xl"
                            title="Edit profile picture"
                          >
                            ✏️
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* OK/Cancel buttons - only show when file is selected */}
                  {isOwner && avatarFile && (
                    <div className="w-full flex gap-3">
                      <button
                        onClick={() => {
                          setAvatarFile(null);
                          setAvatarPreview(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          // Revert to original image (will be shown since avatarPreview is null)
                        }}
                        disabled={uploadingAvatar}
                        className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          setUploadingAvatar(true);
                          try {
                            const formData = new FormData();
                            formData.append('avatar', avatarFile);

                            const headers = getAuthHeaders();
                            const response = await fetch(`/api/heroes/${id}/avatar`, {
                              method: 'PUT',
                              headers: {
                                ...headers
                              },
                              body: formData
                            });

                            const data = await response.json();

                            if (data.success) {
                              success('Profile picture updated successfully!');
                              setAvatarFile(null);
                              setAvatarPreview(null);
                              // Update originalImageUrl to the newly uploaded image
                              setOriginalImageUrl(data.data.image_url);
                              // Clear the file input
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                              // Refresh hero data
                              await fetchWarrior();
                            } else {
                              error(data.error || 'Failed to update profile picture');
                            }
                          } catch (error) {
                            console.error('Error updating avatar:', error);
                            error('Failed to update profile picture. Please try again.');
                          } finally {
                            setUploadingAvatar(false);
                          }
                        }}
                        disabled={uploadingAvatar}
                        className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-colors"
                      >
                        {uploadingAvatar ? 'Uploading...' : 'OK'}
                      </button>
                    </div>
                  )}
                  
                  {/* Help text - reserve space so card doesn't grow/shrink */}
                  <div className="h-5 flex items-center justify-center">
                    {isOwner && !avatarFile && showHelpText && (
                      <p className="text-xs text-gray-400 text-center">
                        JPEG or PNG, max 2048x2048, 5MB
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'attributes' && isOwner && (
            <AttributePanel 
              hero={hero} 
              onAttributeUpdate={handleAttributeUpdate}
            />
          )}

          {activeTab === 'strategies' && isOwner && (
            <StrategySliders 
              hero={hero} 
              onStrategyUpdate={handleStrategyUpdate}
            />
          )}

          {activeTab === 'loot' && (
            <LootTab heroId={hero.id} isOwner={isOwner} themeId={hero.theme_id || hero.themeId || null} />
          )}

          {activeTab === 'frequency' && isOwner && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-6">Battle Frequency Settings</h3>
              
              {/* Current Frequency Display */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-white mb-4">Current Setting</h4>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-6 h-6 rounded ${getFrequencyColor(hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6).split(' ')[0]}`}></div>
                    <div>
                      <div className="text-white font-semibold text-lg">
                        {getFrequencyLabel(hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6)}
                      </div>
                      <div className="text-gray-400">
                        {hero.frequency === 0 ? 'This hero will never be selected for battles' :
                         hero.frequency === 3 ? 'This hero will rarely be selected for battles' :
                         hero.frequency === 6 ? 'This hero will be selected at normal frequency' :
                         hero.frequency === 11 ? 'This hero will be frequently selected for battles' :
                         'This hero will be selected at normal frequency'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Frequency Selection */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-white mb-4">Select Frequency</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { freq: 0, label: 'Never', emoji: '🚫', color: 'red', description: 'DO NOT USE - Hero will never be selected' },
                    { freq: 3, label: 'Seldom', emoji: '🟠', color: 'orange', description: 'Low priority - Rarely selected' },
                    { freq: 6, label: 'Often', emoji: '🟡', color: 'yellow', description: 'Default setting - Normal frequency' },
                    { freq: 11, label: 'Frequent', emoji: '🟢', color: 'green', description: 'High priority - Frequently selected' }
                  ].map(({ freq, label, emoji, color, description }) => (
                    <button
                      key={freq}
                      onClick={() => handleFrequencyUpdate(freq)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        (hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6) === freq
                          ? `border-${color}-500 bg-${color}-500/20`
                          : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">{emoji}</div>
                        <div className="text-white font-semibold">{label}</div>
                        <div className="text-gray-400 text-sm mt-1">{description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Frequency Legend */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-3">Frequency Guide</h4>
                <div className="space-y-2 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span><strong>Never (0):</strong> Hero will never be selected for battles</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-500 rounded"></div>
                    <span><strong>Seldom (3):</strong> Hero will rarely be selected (low priority)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                    <span><strong>Often (6):</strong> Hero will be selected at normal frequency (default)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span><strong>Frequent (11):</strong> Hero will be frequently selected (high priority)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <BattleHistoryTable heroId={hero.id} />
          )}

          {activeTab === 'stats' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Battle Frequency</h3>
                  {isOwner && (
                    <div className="flex gap-1">
                      {[0, 3, 6, 11].map((freq) => (
                        <button
                          key={freq}
                          onClick={() => handleFrequencyUpdate(freq)}
                          className={`w-8 h-8 rounded text-xs font-bold text-white transition-colors ${
                            (hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6) === freq
                              ? getFrequencyColor(freq)
                              : getFrequencyColorDimmed(freq)
                          }`}
                          title={getFrequencyLabel(freq)}
                        >
                          {freq === 0 ? '🚫' : freq === 3 ? 'S' : freq === 6 ? 'O' : 'F'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-4">
                  <div className={`w-4 h-4 rounded ${getFrequencyColor(hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6).split(' ')[0]}`}></div>
                  <div>
                    <div className="text-white font-semibold">
                      {getFrequencyLabel(hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6)}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {hero.frequency === 0 ? 'This hero will never be selected for battles' :
                       hero.frequency === 3 ? 'This hero will rarely be selected for battles' :
                       hero.frequency === 6 ? 'This hero will be selected at normal frequency' :
                       hero.frequency === 11 ? 'This hero will be frequently selected for battles' :
                       'This hero will be selected at normal frequency'}
                    </div>
                  </div>
                </div>
              </div>

              {isOwner && hasStatReset && (
                <div className="mt-8 pt-6 border-t border-gray-600">
                  <h4 className="text-lg font-semibold text-white mb-4 text-orange-400">Reset Attributes</h4>
                  <div className="bg-orange-900/20 border border-orange-700 rounded-lg p-4">
                    <h5 className="text-orange-300 font-medium mb-2">Reset All Spent XP</h5>
                    <p className="text-gray-300 text-sm mb-4">
                      Reset all attribute points and refund all XP you've spent. This will return your hero's attributes to their base values.
                    </p>
                    <div className="flex justify-center">
                      <button
                        onClick={handleResetStats}
                        disabled={resetting}
                        className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        {resetting ? 'Resetting...' : 'Reset All Attributes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {isOwner && (
                <div className="mt-8 pt-6 border-t border-gray-600">
                  <h4 className="text-lg font-semibold text-white mb-4 text-red-400">Danger Zone</h4>
                  <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                    <h5 className="text-red-300 font-medium mb-2">Delete Hero</h5>
                    <p className="text-gray-300 text-sm mb-4">
                      Once you delete this hero, there is no going back. Please be certain.
                    </p>
                    <div className="flex justify-center">
                      <button
                        onClick={handleDeleteWarrior}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        Delete Hero Permanently
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroDetail;
